import React, { useRef, useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";

const generateData = (length = 50000) => {
  return Array.from({ length }, (_, i) => [
    i,
    Math.sin(i / 1000) * 10 + Math.random() * 2,
  ]);
};

const SCROLL_WIDTH = 3000; // Largeur fictive de la div scrollable (impacte la précision et la sensibilité)
const DATA_TOTAL = 150000;
const WINDOW_SIZE = 20000;

const DualScrollChart = () => {
  const fullData = useRef(generateData(DATA_TOTAL));
  const scrollRef = useRef(null);

  const [scrollMode, setScrollMode] = useState("expansion"); // "expansion" ou "window"
  const [xRange, setXRange] = useState({ min: 0, max: WINDOW_SIZE });
  const [chartWidth, setChartWidth] = useState(window.innerWidth - 40);

  // Met à jour la taille du graphique quand la fenêtre est redimensionnée
  useEffect(() => {
    const handleResize = () => setChartWidth(window.innerWidth - 40);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fonction de scroll pour le mode "expansion" (xMin = 0, xMax augmente)
  const handleScrollExpansion = () => {
    const el = scrollRef.current;
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const newMax = WINDOW_SIZE + Math.round(scrollRatio * (DATA_TOTAL - WINDOW_SIZE));
    setXRange({ min: 0, max: newMax });
  };

  // Fonction de scroll pour le mode "window" (xMin et xMax glissent)
  const handleScrollWindow = () => {
    const el = scrollRef.current;
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const newMin = Math.round(scrollRatio * (DATA_TOTAL - WINDOW_SIZE));
    setXRange({ min: newMin, max: newMin + WINDOW_SIZE });
  };

  // Redirige le scroll selon le mode actif
  const handleScroll = () => {
    if (scrollMode === "expansion") {
      handleScrollExpansion();
    } else {
      handleScrollWindow();
    }
  };

  // Filtrer les données visibles selon le range X (min, max)
  const displayedData = fullData.current.filter(
    ([x]) => x >= xRange.min && x <= xRange.max
  );

  // Options ECharts dynamiques
  const option = {
    animation: false,
    grid: { left: 50, right: 20, top: 20, bottom: 40 },
    xAxis: {
      type: "value",
      min: xRange.min,
      max: xRange.max,
      axisLabel: { formatter: (val) => `${val}s` },
    },
    yAxis: {
      type: "value",
      min: -15,
      max: 15,
    },
    series: [
      {
        type: "line",
        data: displayedData,
        showSymbol: false,
        lineStyle: { width: 1 },
      },
    ],
    tooltip: {
      trigger: "axis",
    },
  };

  return (
    <div style={{ padding: 20, position: "relative" }}>
      <h3>
        Mode de scroll : <strong>{scrollMode}</strong>
      </h3>
      <p>
        Plage affichée : {xRange.min} → {xRange.max} secondes
      </p>

      {/* Bouton pour changer de mode */}
      <button
        onClick={() =>
          setScrollMode((prev) =>
            prev === "expansion" ? "window" : "expansion"
          )
        }
        style={{
          marginBottom: 10,
          padding: "6px 12px",
          fontSize: "1rem",
          cursor: "pointer",
        }}
      >
        Changer de mode de scroll
      </button>

      <div style={{ position: "relative" }}>
        {/* Graphique principal */}
        <ReactECharts
          option={option}
          style={{ width: chartWidth, height: 400 }}
          notMerge={true}
          lazyUpdate={true}
        />

        {/* Couche scrollable superposée */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 400,
            overflowX: "scroll",
            zIndex: 10,
            background: "rgba(0, 0, 0, 0.02)", // léger fond visible pour debug
            pointerEvents: "auto",
          }}
        >
          {/* Élément large pour permettre le scroll */}
          <div style={{ width: `${SCROLL_WIDTH}px`, height: "100%" }} />
        </div>
      </div>

      <p style={{ fontSize: 14, marginTop: 8 }}>
        Scrollez horizontalement dans le graphique pour {scrollMode === "expansion"
          ? "étendre la plage de données visible"
          : "naviguer dans la fenêtre glissante"}.
      </p>
    </div>
  );
};

export default DualScrollChart;
